import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Chip } from '@material-ui/core'

import { useQueryPaginator } from 'src/components/Table'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { EventListTable } from 'src/components/Table/list/Event'
import { FreightShipmentBarcodedUnitListTable } from 'src/components/Table/list/FreightShipmentBarcodedUnit'
import { FreightShipmentPalletListTable } from 'src/components/Table/list/FreightShipmentPallet'
import { FreightShipmentUnitListTable } from 'src/components/Table/list/FreightShipmentUnit'
import { FreightShipmentDetailTable } from 'src/components/Table/detail/FreightShipment'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { FreightShipmentDialog } from 'src/modules/supply_chain/components/freight/ShipmentDialog'
import { TruckloadDialog } from 'src/modules/supply_chain/components/freight/TruckloadDialog'
import { Workflow } from 'src/modules/supply_chain/components/Workflow'

import { skuit } from 'src/services/skuit'
import { FreightShipment, Tag } from 'src/api/models'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface ParamsProps {
  guid: string
}

export const ShipmentPage = () => {
  const { guid } = useParams<ParamsProps>()
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)
  const [truckloadDialogOpen, setTruckloadDialogOpen] = useState(false)

  const shipment = useQuery<FreightShipment>(['shipment-details', guid], () =>
    skuit.sc.freight.shipments.retrieve(guid)
  )

  const attachmentsPaginator = useQueryPaginator(
    'attachment-list',
    () => skuit.sc.freight.shipments.attachments.list(guid),
    {
      params: { page_size: 4, shipments: guid },
    }
  )
  const unitsPaginator = useQueryPaginator(
    'shipment-unit-list',
    skuit.sc.freight.shipments.units.list,
    { params: { page_size: 5, shipments: guid } }
  )
  const barcodedPaginator = useQueryPaginator(
    'shipment-barcoded-unit-list',
    skuit.sc.freight.shipments.barcodedUnits.list,
    { params: { page_size: 5, shipments: guid } }
  )
  const palletsPaginator = useQueryPaginator(
    'shipment-pallet-list',
    skuit.sc.freight.shipments.pallets.list,
    { params: { page_size: 5, shipments: guid } }
  )
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  const refresh = () => {
    shipment.refetch()
    attachmentsPaginator.refresh()
    unitsPaginator.refresh()
    barcodedPaginator.refresh()
    palletsPaginator.refresh()
    eventsPaginator.refresh()
  }

  return (
    <>
      {truckloadDialogOpen && (
        <TruckloadDialog
          open={truckloadDialogOpen}
          handleClose={() => setTruckloadDialogOpen(false)}
          shipment={shipment.data}
        />
      )}
      <DetailPage
        obj={shipment}
        title="Freight Shipment"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Freight Shipments', url: '/freight/shipments' },
        ]}
        detailComponent={<FreightShipmentDetailTable obj={shipment} refresh={refresh} />}
        secondaryComponent={
          <Workflow
            title="Shipment Workflow"
            loading={shipment.isFetching}
            workflow={shipment.data?.workflow || []}
            status={shipment.data?.workflowStatus || ''}
            actions={shipment.data?.getWorkflowActions(() => shipment.refetch()) || {}}
          />
        }
        createDialog={{ title: 'New Freight Shipment', Dialog: FreightShipmentDialog }}
        createDialogs={[{ title: 'Create Truckload from Shipment', open: () => setTruckloadDialogOpen(true) }]}
      >
        {addAttachmentDialogOpen && (
          <AddAttachmentsDialog
            onSuccess={refresh}
            addAttachment={(config) =>
              skuit.sc.freight.shipments.attachments.create(guid, config?.data, config)
            }
            open={addAttachmentDialogOpen}
            handleClose={() => {
              setAddAttachmentDialogOpen(false)
            }}
          />
        )}
        <PaginatedFilePreviewGallery
          title="Attachments"
          paginator={attachmentsPaginator}
          getIdent={(item) => item.guid}
          onAdd={() => {
            setAddAttachmentDialogOpen(true)
          }}
          renderItem={(item) => {
            return (
              <>
                <GenericPreview url={item.file} />
                <div>
                  {item.tags.map((tag: Tag) => (
                    <Chip key={tag.slug} label={tag.name} />
                  ))}
                </div>
              </>
            )
          }}
        />
        <FreightShipmentUnitListTable
          paginator={unitsPaginator}
          title="Units"
          fields={['productName', 'product', 'sku', 'batch', 'numUnits', 'container']}
        />
        <FreightShipmentBarcodedUnitListTable
          paginator={barcodedPaginator}
          title="Units (Barcoded)"
          fields={['unit', 'product']}
        />
        <FreightShipmentPalletListTable
          paginator={palletsPaginator}
          title="Pallets"
          fields={['pallet', 'client', 'clientID', 'ssccCode', 'skus', 'path']}
        />
        <EventListTable
          paginator={eventsPaginator}
          title="Freight Shipment Events"
          fields={['type', 'data', 'by', 'on']}
        />
      </DetailPage>
    </>
  )
}
